import type {
    CheckboxButtonData,
    DefaultButtonData,
    LoginEduFormTrigger,
    LoginEduWidgetData,
    LoginWidgetButtons,
    RegistrationButtonData,
} from '@web/widgets/login-edu/type/login-edu-widget.type';
import { type AuthorizationButtonData } from '@web/widgets/login-edu/type/login-edu-widget.type';

import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import type { FieldTree } from '@angular/forms/signals';

import type { WebLegacyWidgetEntity } from '@web/types';
import type { FormElement } from '@common/types/form-element.type';
import type { ExtendedPositionLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import type { ESize } from '@common/enums';
import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
    EButtonStyleType,
} from '@common/enums';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { isLoginEduNewUser } from '@web/widgets/login-edu/constants/login-edu.const';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import type { FormCssVars } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import type { LoginEduFormViewData } from '@sites/entity-core/src/widget/login-edu/styles/login-edu.css-vars';
import {
    getLoginEduButtonClassList,
    getLoginEduButtonCssVars,
    getLoginEduButtonGroupCssVars,
    getLoginEduButtonWrapperCssVars,
    getLoginEduFormRootCssVars,
    getLoginEduFormViewClasses,
    getLoginEduTriggerCssClasses,
    getLoginEduTriggerCssVars,
} from '@sites/entity-core/src/widget/login-edu/styles/login-edu.css-vars';
import { ELoginEduView } from '@common/enums/login-edu-view-form.enum';
import { ELoginEduDefaultForm } from '@common/enums/login-edu-default-form.enum';
import { LoginFormViewComponent } from './login-form-view/login-form-view.component';
import { RegisterFormViewComponent } from './register-form-view/register-form-view.component';
import { colorCss } from '@web/widgets/common/css-vars/css-vars.utils';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

@Component({
    selector: 'web-login-edu-view',
    templateUrl: './login-edu-view.component.html',
    styleUrls: ['./login-edu-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RegisterFormViewComponent, LoginFormViewComponent],
})
export class LoginEduViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<LoginEduWidgetData>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public form!: LoginEduForm;

    protected readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    protected readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    private readonly userCreatedAt = toSignal(inject(UserFacade).memberAreaUserCreatedAt$, { initialValue: null });

    private readonly isForNewUser = computed(() => isLoginEduNewUser(this.userCreatedAt()));

    /**
     * Live entity straight from the signal form. Until `ngOnInit` runs (and the
     * form is materialised), fall back to the input snapshot.
     */
    public readonly entity = computed<WebLegacyWidgetEntity<LoginEduWidgetData>>(
        () => (this.form?._raw().value() as unknown as WebLegacyWidgetEntity<LoginEduWidgetData>) ?? this.widget()
    );

    /** True when the widget lives inside a zero block. */
    public readonly isZero = computed<boolean>(() => this.entity().type === 'zero');

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<LoginEduForm>(this.widget().id, this.destroyRef);

        if (this.isZero()) {
            this.setupZeroHeightSync();
        }
    }

    /**
     * Zero-block login-edu widgets take their width from the zero host (the root
     * fills `--content-width`) and grow in height with their content: the rendered
     * root's height is mirrored back into the extended-position grid so the zero
     * host's selection frame and boundary math track the real size — the button
     * view's `setupZeroHeightSync` pattern.
     */
    private setupZeroHeightSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => this.syncZeroHeightFromDom(),
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    private syncZeroHeightFromDom(): void {
        const root = this.elementRef.nativeElement.firstElementChild as HTMLElement | null;
        if (!root) return;

        this.setHeight(parseFloat(getComputedStyle(root).height));
    }

    /**
     * Mirror the measured height into the extended-position grid — the active
     * device only, or all three on desktop. The change-comparison on every write
     * is what breaks the measure → write → re-measure feedback loop.
     */
    private setHeight(height: number): void {
        if (!Number.isFinite(height) || height <= 0) return;

        // The form's position accessor is union-typed (simple grid on default
        // placement) — this code path only runs for zero widgets.
        const positionField = this.form.position as unknown as FieldTree<ExtendedPositionLegacy>;
        const position = positionField().value();

        const write = () => {
            if (this.isDeviceMobileSig()) {
                if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
                return;
            }

            if (this.isDeviceTabletSig()) {
                if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
                return;
            }

            if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
            if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
            if (position.height !== height) positionField.height().value.set(height);
        };

        // Batch the writes into one store dispatch — unless an outer transaction
        // (settings panel editing) is already open: a nested `transact` would
        // commit the outer snapshot prematurely.
        if (this.form.inTransaction()) {
            write();
        } else {
            this.form.transact(write);
        }
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public getTriggerEffectClass(trigger: LoginEduFormTrigger): string {
        const effects = trigger?.additionalEffects;
        if (!effects?.isActive) {
            return '';
        }

        let name: string = '';
        if (effects.effectCondition === EButtonAdditionalEffectsCondition.hover) {
            switch (effects.hoverEffects) {
                case EButtonAdditionalHoverEffectsTypes.bounce:
                case EButtonAdditionalHoverEffectsTypes.shake:
                case EButtonAdditionalHoverEffectsTypes.decrease:
                case EButtonAdditionalHoverEffectsTypes.increase:
                    name = effects.hoverEffects;
                    break;
                default:
                    break;
            }
        } else if (effects.effectCondition === EButtonAdditionalEffectsCondition.static) {
            switch (effects.staticEffects) {
                case EButtonAdditionalStaticEffectsTypes.shine:
                case EButtonAdditionalStaticEffectsTypes.twinkle:
                    name = effects.staticEffects;
                    break;
                default:
                    break;
            }
        }

        return name ? `button_effect_${name}` : '';
    }

    public getButtonClass(button: DefaultButtonData): string {
        return getLoginEduButtonClassList(button, button.size, button.styleType);
    }

    public getButtonVars(button: DefaultButtonData): FormCssVars {
        return getLoginEduButtonCssVars(button, colorCss);
    }

    public getRegistrationButtonClass(button: RegistrationButtonData): string {
        return getLoginEduButtonClassList(
            button,
            button?.registrationButton?.size,
            button?.registrationButton?.styleType
        );
    }

    public isButtonActive(button: Pick<LoginWidgetButtons, 'buttonType' | 'isActive'>): boolean {
        if (button.buttonType === 'default') {
            return true;
        }

        return button.isActive;
    }

    public readonly isAuthorizationTriggerActive = computed<boolean>(
        () => !this.isForNewUser() && this.form.isAuthorizationTriggerActive()
    );

    public readonly isRegisterForm = computed<boolean>(
        () => this.isForNewUser() || this.form.defaultForm().value() === ELoginEduDefaultForm.register
    );

    public readonly isLoginForm = computed<boolean>(
        () => !this.isForNewUser() && this.form.defaultForm().value() === ELoginEduDefaultForm.login
    );

    public readonly isShowAuthorizationFormLink = computed<boolean>(
        () => !this.isForNewUser() && this.form.isShowAuthorizationFormLink()
    );

    /** Alignment of the trigger/button row (the old `button-group` JSS key). */
    public readonly buttonGroupVars = computed<FormCssVars>(() =>
        getLoginEduButtonGroupCssVars({
            horizontalPosition: this.form.horizontalPosition().value(),
        } as LoginEduWidgetData)
    );

    private readonly inputSettings = computed(
        () => this.form.inputSettings().value() as unknown as LoginEduWidgetData['inputSettings']
    );

    /**
     * Variables the inline forms set once on their root; they cascade to every
     * field and control below.
     */
    private readonly formRootVars = computed<FormCssVars>(() =>
        getLoginEduFormRootCssVars(this.inputSettings(), colorCss)
    );

    // The zod-inferred element type has nullable/optional leaves where the app's
    // `FormElement` union requires them — cast at the boundary.
    private readonly signUpElements = computed<FormElement[]>(
        () => this.form.signUpElements().value() as unknown as FormElement[]
    );

    private readonly signInElements = computed<FormElement[]>(
        () => this.form.signInElements().value() as unknown as FormElement[]
    );

    public readonly registrationBtnStyles = computed<string>(() => {
        const button = this.form.registrationButton();

        return button ? this.getRegistrationButtonClass(button as unknown as RegistrationButtonData) : '';
    });

    public readonly authorizationBtnStyles = computed<string>(() => {
        const button = this.form.authorizationButton();

        return button ? this.getButtonClass(button as unknown as DefaultButtonData) : '';
    });

    public readonly registrationButtonData = computed<LoginEduFormViewData>(() => {
        const button = this.form.registrationButton();

        return {
            ...getLoginEduFormViewClasses(this.inputSettings()),
            rootVars: this.formRootVars(),
            wrapperVars: getLoginEduButtonWrapperCssVars(button?.registrationButton?.size as ESize | undefined),
            buttonVars: button ? getLoginEduButtonCssVars(button as unknown as LoginWidgetButtons, colorCss) : {},
            buttonClasses: this.registrationBtnStyles(),
            buttonLabel: this.form.registrationButtonLabel(),
            checkboxes: this.form.checkboxes().value() as unknown as CheckboxButtonData[],
            isShowCheckboxes: Boolean(this.form.registrationCheckbox().value()),
            elements: this.signUpElements(),
        };
    });

    public readonly authorizationButtonData = computed<LoginEduFormViewData>(() => {
        const button = this.form.authorizationButton();

        return {
            ...getLoginEduFormViewClasses(this.inputSettings()),
            rootVars: this.formRootVars(),
            wrapperVars: getLoginEduButtonWrapperCssVars(button?.size as ESize | undefined),
            buttonVars: button ? getLoginEduButtonCssVars(button as unknown as LoginWidgetButtons, colorCss) : {},
            buttonClasses: this.authorizationBtnStyles(),
            buttonLabel: this.form.authorizationButtonLabel(),
            elements: this.signInElements(),
        };
    });

    /** Trigger button variables, keyed by which of the two triggers is rendered. */
    public getTriggerVars(trigger: LoginEduFormTrigger): FormCssVars {
        return getLoginEduTriggerCssVars(trigger, colorCss);
    }

    public getTriggerButtonClass(trigger: LoginEduFormTrigger, type: 'registration' | 'authorization'): string {
        if (!trigger) {
            return type === 'authorization' ? this.authorizationBtnStyles() : this.registrationBtnStyles();
        }

        const base = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[trigger.size]}`;
        const preset = trigger.styleType === EButtonStyleType.custom ? '' : BUTTON_TYPE_CLASS_MAP[trigger.styleType];
        const effectClass = this.getTriggerEffectClass(trigger);

        return `${base} ${preset} ${getLoginEduTriggerCssClasses(trigger)} ${effectClass}`.replace(/\s+/g, ' ').trim();
    }

    // Exposed for the template, which branches on the form's view-mode values directly.
    protected readonly ELoginEduView = ELoginEduView;
}
